import * as React from "react";
import { cn } from "@/lib/utils";
import { CopyIcon } from "../icons";

export interface WalletAddressFieldProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "children"> {
  /**
   * Label for the field
   * @example "Wallet address :" | "SNS :"
   */
  label: string;
  /**
   * Display value (can be truncated for UI)
   * @example "aVudDp3w...GocQz4nB" | "ipseitR.sol"
   */
  displayValue: string;
  /**
   * Full value to copy to clipboard
   * @example "aVudDp3w1234567890GocQz4nB" (full wallet address)
   */
  fullValue?: string;
  /**
   * Callback when value is copied
   */
  onCopy?: () => void;
}

/**
 * Wallet Address Field - Copyable address display with label
 *
 * A read-only field that displays wallet addresses or name services (like ENS).
 * Clicking the field copies the full address to clipboard. Supports both wallet
 * address variant and SNS/name service variant.
 *
 * Follows ANYW3AR Figma design specifications:
 * - Wallet variant: node-id 296-6335
 * - SNS variant: node-id 260-1978
 *
 * @example
 * ```tsx
 * // Wallet address variant
 * <WalletAddressField
 *   label="Wallet address :"
 *   displayValue="aVudDp3w...GocQz4nB"
 *   fullValue="aVudDp3w1234567890GocQz4nB"
 *   onCopy={() => console.log('Copied!')}
 * />
 *
 * // SNS/Name service variant
 * <WalletAddressField
 *   label="SNS :"
 *   displayValue="ipseitR.sol"
 *   fullValue="ipseitR.sol"
 * />
 * ```
 */
export const WalletAddressField = React.forwardRef<
  HTMLButtonElement,
  WalletAddressFieldProps
>(
  (
    {
      className,
      label,
      displayValue,
      fullValue,
      onCopy,
      ...props
    },
    ref
  ) => {
    const valueToCopy = fullValue || displayValue;

    const handleCopy = async () => {
      try {
        await navigator.clipboard.writeText(valueToCopy);
        onCopy?.();
      } catch (error) {
        console.error("Failed to copy to clipboard:", error);
      }
    };

    return (
      <button
        ref={ref}
        type="button"
        onClick={handleCopy}
        className={cn(
          "flex items-center justify-between",
          "px-2 py-0 rounded-[0.3125rem]",
          "bg-transparent border-none",
          "transition-colors cursor-pointer",
          "hover:bg-gray-50",
          "focus:outline-none focus:ring-2 focus:ring-gray-600 focus:ring-offset-1",
          "active:bg-gray-100",
          "w-full",
          className
        )}
        {...props}
      >
        {/* Left side: Label + Display Value */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="flex flex-col gap-1 items-start flex-1 min-w-0">
            {/* Label */}
            <div className="flex flex-col justify-end">
              <p className="font-montserrat text-[0.75rem] font-normal leading-5 text-gray-500">
                {label}
              </p>
            </div>
            {/* Display Value */}
            <div className="flex flex-col justify-end">
              <p className="font-montserrat text-[0.9375rem] font-normal leading-5 text-gray-900 truncate">
                {displayValue}
              </p>
            </div>
          </div>
        </div>

        {/* Right side: Copy Icon */}
        <div className="flex items-start px-0 py-2 gap-4 shrink-0">
          <CopyIcon size={12} className="text-gray-500" aria-hidden="true" />
        </div>
      </button>
    );
  }
);

WalletAddressField.displayName = "WalletAddressField";
